import type { LucideIcon } from 'lucide-react';
import {
  Hammer,
  Landmark,
  Target,
  FlaskConical,
  VenetianMask,
  Vault,
  ShieldCheck,
  Bug,
  Rocket,
  ClipboardList,
  Cat,
  Orbit,
  Layers,
  Building2,
  Scale,
  ShoppingCart,
  Utensils,
  Zap,
  Store,
  CreditCard,
  Banknote,
  Leaf,
  TrendingUp,
  Drama,
  CalendarDays,
  Split,
  Crosshair,
  MessageSquareText,
  Unlink,
  Bird,
  RefreshCw,
  Puzzle,
  Link2,
  Receipt,
  GraduationCap,
  ClipboardCheck,
  Dna,
  Shapes,
  ScanEye,
  FileBarChart,
  TrendingDown,
  Workflow,
  FileSearch,
  Skull,
} from 'lucide-react';

export interface Mission {
  id: string;
  hero: string;
  label: string;
  icon: LucideIcon;
  title: string;
  description: string;
  accept: string;
  maxSizeLabel: string;
  color: string;
}

const missions: Mission[] = [
  {
    id: 'excel-forge',
    hero: "Thor's Hammer",
    label: 'Thor\'s Hammer Excel Forge',
    icon: Hammer,
    title: 'Excel Forge',
    description: 'Forge and reshape raw Excel workbooks into clean, structured output.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#f59e0b',
  },
  {
    id: 'remittances',
    hero: 'Stark Financial Hub',
    label: 'Stark Financial Hub Remittances',
    icon: Landmark,
    title: 'Zental Supplier Remittances',
    description: 'Turn a supplier remittance workbook into one payment summary per paid contact (ZIP).',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#dc2626',
  },
  {
    id: 'amazon-sales',
    hero: 'Hawkeye Sales Update',
    label: 'Hawkeye Sales Update (Amazon)',
    icon: Target,
    title: 'Amazon Sales Update',
    description: 'Pull the latest Amazon sales data and reconcile it against inventory.',
    accept: '.xlsx,.csv',
    maxSizeLabel: '10MB per file • XLSX, CSV',
    color: '#7c3aed',
  },
  {
    id: 'payroll-lab',
    hero: "Banner's Payroll Lab",
    label: 'Banner\'s Payroll Lab',
    icon: FlaskConical,
    title: "Banner's Payroll Lab",
    description: 'Crunch payroll inputs and produce a validated payroll run.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#16a34a',
  },
  {
    id: 'intel-fusion',
    hero: "Black Widow's Intel Fusion",
    label: 'Black Widow\'s Intel Fusion',
    icon: VenetianMask,
    title: "Black Widow's Intel Fusion",
    description: 'Combine multiple Excel/CSV files with the same columns into one dataset.',
    accept: '.xlsx,.csv',
    maxSizeLabel: '10MB per file • XLSX, CSV',
    color: '#db2777',
  },
  {
    id: 'data-vault',
    hero: "Nick Fury's Data Vault",
    label: 'Nick Fury\'s Data Vault',
    icon: Vault,
    title: "Nick Fury's Data Vault",
    description: 'Anonymize names and sensitive data in files, and restore them later with the mapping.',
    accept: '.xlsx,.xls,.csv,.pdf',
    maxSizeLabel: '10MB per file • XLSX, CSV, PDF',
    color: '#0ea5e9',
  },
  {
    id: 'attendance',
    hero: "Captain's Roll Call",
    label: 'Captain\'s Roll Call Attendance',
    icon: ShieldCheck,
    title: "Captain's Roll Call",
    description: 'Take attendance rosters and turn them into daily summaries.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#2563eb',
  },
  {
    id: 'cash-vault',
    hero: "Ant-Man's Cash Vault",
    label: 'Ant-Man\'s Cash Vault (Cash Tracker)',
    icon: Bug,
    title: "Ant-Man's Cash Vault",
    description: 'Track cash movements across accounts, however small.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#ea580c',
  },
  {
    id: 'payroll-command',
    hero: 'War Machine Payroll Command',
    label: 'War Machine Payroll Command (Payroll Agent)',
    icon: Rocket,
    title: 'War Machine Payroll Command',
    description: 'Extract payroll PDFs and Excel files and reconcile them to the nominal ledger.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#64748b',
  },
  {
    id: 'hr-consolidator',
    hero: 'Maria Hill HR Consolidator',
    label: 'Maria Hill HR Consolidator (HR Data)',
    icon: ClipboardList,
    title: 'Maria Hill HR Consolidator',
    description: 'Consolidate HR records from multiple sources into one file.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#0891b2',
  },
  {
    id: 'rental-converter',
    hero: "Black Panther's Rental Converter",
    label: 'Black Panther\'s Rental Converter',
    icon: Cat,
    title: "Black Panther's Rental Converter",
    description: 'Convert rental statement PDFs into a reconciled Excel workbook.',
    accept: '.pdf,.xlsx',
    maxSizeLabel: 'PDF statement + XLSX template',
    color: '#8b5cf6',
  },
  {
    id: 'remittance-reader',
    hero: 'Quicksilver’s Remittance Reader',
    label: 'Quicksilver’s Remittance Reader',
    icon: FileSearch,
    title: 'Quicksilver’s Remittance Reader',
    description: 'Read remittance advice PDFs of any layout and extract date, reference, type and amount into Excel.',
    accept: '.pdf',
    maxSizeLabel: '10MB per file • PDF',
    color: '#0d9488',
  },
  {
    id: 'accounts-checker',
    hero: 'Mantis Accounts Checker',
    label: 'Mantis Accounts Checker',
    icon: FileSearch,
    title: 'Mantis Accounts Checker',
    description: 'Check each current-year description was posted to the same account as last year, from one two-year workbook.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#15803d',
  },
  {
    id: 'expenditure-ledger',
    hero: 'Red Skull Expenditure Ledger',
    label: 'Red Skull Expenditure Ledger',
    icon: Skull,
    title: 'Red Skull Expenditure Ledger',
    description:
      'Extract each particular’s TOTAL from the service charge expenditure summary PDF and add its accrual, prepayment and reclass adjustments from the expenditure ledgers.',
    accept: '.pdf,.xlsx,.xls,.csv',
    maxSizeLabel: '10MB per file • PDF + XLSX, XLS or CSV',
    color: '#b91c1c',
  },
  {
    id: 'ledger-transformer',
    hero: "Doctor Strange's Nominal Ledger Transformer",
    label: 'Doctor Strange\'s Nominal Ledger Transformer',
    icon: Orbit,
    title: "Doctor Strange's Nominal Ledger Transformer",
    description: 'Transform nominal ledger exports (Xero, QuickBooks, Sage) into one combined workbook, one sheet per year.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#9333ea',
  },
  {
    id: 'supplier-grouping',
    hero: "Okoye's Supplier Grouping",
    label: "Okoye's Supplier Grouping",
    icon: Layers,
    title: "Okoye's Supplier Grouping",
    description: 'Group an AP Reconciliation report by supplier, with subtotals and a grand total.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#ca8a04',
  },
  {
    id: 'fixed-assets',
    hero: "Valkyrie's Fixed Asset Register",
    label: "Valkyrie's Fixed Asset Register",
    icon: Building2,
    title: "Valkyrie's Fixed Asset Register",
    description: 'Consolidate fixed asset workbooks into one register, grouped by account.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#0f766e',
  },
  {
    id: 'sumup-recon',
    hero: "Gamora's Sales Reconciler",
    label: "Gamora's Sales Reconciler (SumUp)",
    icon: Scale,
    title: "Gamora's Sales Reconciler",
    description: 'Reconcile SumUp sales reports against payouts, month by month, with a VAT check.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#4f46e5',
  },
  {
    id: 'bank-recon',
    hero: 'Heimdall’s Bank Reconciler',
    label: 'Heimdall’s Bank Reconciler',
    icon: Banknote,
    title: 'Heimdall’s Bank Reconciler',
    description: 'Match a General Ledger against a bank statement and report the variation.',
    accept: '.xlsx,.xls',
    maxSizeLabel: '10MB per file • XLSX, XLS',
    color: '#0369a1',
  },
  {
    id: 'aged-payables',
    hero: "Groot's Payables Grove",
    label: "Groot's Payables Grove",
    icon: Leaf,
    title: "Groot's Payables Grove",
    description: 'Combine Aged Payables files into one workbook with duplicate and direct-debit checks.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#15803d',
  },
  {
    id: 'weekly-sales',
    hero: "Ellis' Sales Command",
    label: "Ellis' Sales Command",
    icon: TrendingUp,
    title: "Ellis' Sales Command",
    description: 'Merge the weekly P&L input into the rolling Master workbook and refresh its Summary.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#be185d',
  },
  {
    id: 'supplier-recon',
    hero: "Loki's Supplier Reconciler",
    label: "Loki's Supplier Reconciler",
    icon: Drama,
    title: "Loki's Supplier Reconciler",
    description: 'Reconcile a Xero Bill Activity ledger against supplier statement PDFs or Excel statements.',
    accept: '.xlsx,.xls',
    maxSizeLabel: '10MB per file • XLSX, XLS',
    color: '#7c3aed',
  },
  {
    id: 'sme-tracker',
    hero: 'Star-Lord’s SME Daily Tracker',
    label: 'Star-Lord’s SME Daily Tracker',
    icon: CalendarDays,
    title: 'Star-Lord’s SME Daily Tracker',
    description: 'Convert daily tracker workbooks to the standard template, merged or one output per file.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#0d9488',
  },
  {
    id: 'revenue-splitter',
    hero: "Wanda's Revenue Splitter",
    label: "Wanda's Revenue Splitter",
    icon: Split,
    title: "Wanda's Revenue Splitter",
    description: 'Split a transaction workbook into Accrud and Deford outputs with monthly spread.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#e11d48',
  },
  {
    id: 'payroll-match',
    hero: "Bucky's Payroll Match",
    label: "Bucky's Payroll Match",
    icon: Crosshair,
    title: "Bucky's Payroll Match",
    description: 'Fuzzy-match payroll runs against net wages bank transactions.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#475569',
  },
  {
    id: 'notes-queries',
    hero: "Jarvis's Notes & Queries",
    label: "Jarvis's Notes & Queries",
    icon: MessageSquareText,
    title: "Jarvis's Notes & Queries",
    description: 'Build the Notes & Queries workbook from account transactions and a mapping sheet.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#2563eb',
  },
  {
    id: 'unmerge-realign',
    hero: "Drax's Unmerge & Realign",
    label: "Drax's Unmerge & Realign",
    icon: Unlink,
    title: "Drax's Unmerge & Realign",
    description: 'Unmerge Sage-style reports and slide shifted amount cells back under their real header.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#b45309',
  },
  {
    id: 'payout-recon',
    hero: "Falcon's Payout Reconciler",
    label: "Falcon's Payout Reconciler",
    icon: Bird,
    title: "Falcon's Payout Reconciler",
    description: 'Reconcile Swiggy/Zomato payouts against your ERP records, order by order.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#ea580c',
  },
  {
    id: 'mis-refresh',
    hero: "Shuri's MIS Refresh Lab",
    label: "Shuri's MIS Refresh Lab",
    icon: RefreshCw,
    title: "Shuri's MIS Refresh Lab",
    description: "Refresh a monthly MIS workbook's data-feed sheets from this month's source files.",
    accept: '.xlsx,.xlsm',
    maxSizeLabel: '10MB per file • XLSX, XLSM',
    color: '#7c2d12',
  },
  {
    id: 'inncontrol',
    hero: "Nebula's Ledger Assembler",
    label: "Nebula's Ledger Assembler",
    icon: Puzzle,
    title: "Nebula's Ledger Assembler",
    description: 'Process a Management Accounts workbook: per-account sheets, summary formats, fixed asset register and a live Balance Sheet.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#4338ca',
  },
  {
    id: 'etb-interlink',
    hero: "Rocket's ETB Interlink Builder",
    label: "Rocket's ETB Interlink Builder",
    icon: Link2,
    title: "Rocket's ETB Interlink Builder",
    description: 'Build the interlinked ETB workbook from the Chart of Accounts, QB trial balances and the Digita AP trial balance PDF.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#c2410c',
  },
  {
    id: 'vat-agent',
    hero: 'Tony’s VAT Command',
    label: 'Tony’s VAT Command',
    icon: Receipt,
    title: 'Tony’s VAT Command',
    description: 'Consolidate VAT return transactions from Xero or QuickBooks exports into one VAT summary workbook.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#0f766e',
  },
  {
    id: 'training-eval',
    hero: "Agent Coulson's Training Evaluation",
    label: "Agent Coulson's Training Evaluation",
    icon: GraduationCap,
    title: "Agent Coulson's Training Evaluation",
    description: 'Compare a Trainee Excel file against an Answer Key and produce a question-by-question scored workbook.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#1d4ed8',
  },
  {
    id: 'trainer-scoring',
    hero: "The Ancient One's Trainer Scoring Board",
    label: "The Ancient One's Trainer Scoring Board",
    icon: ClipboardCheck,
    title: "The Ancient One's Trainer Scoring Board",
    description: "Fill the trainer's score sheet from a trainee's answers: questions matched by index number, every sheet scored.",
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#7c3aed',
  },
  {
    id: 'toa-transformer',
    hero: "The Online Accountants",
    label: "The Online Accountants",
    icon: Dna,
    title: "The Online Accountants",
    description: 'Client Format Transformer for The Online Accountants.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#0891b2',
  },
  {
    id: 'esp-transformer',
    hero: "ESP",
    label: "ESP",
    icon: Shapes,
    title: "ESP",
    description: 'Client Format Transformer for ESP.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#a21caf',
  },
  {
    id: 'fourfifty-transformer',
    hero: "FourFifty",
    label: "FourFifty",
    icon: Layers,
    title: "FourFifty",
    description: 'Client Format Transformer for FourFifty.',
    accept: '.xlsx,.xlsm',
    maxSizeLabel: '10MB per file • XLSX, XLSM',
    color: '#ea580c',
  },
  {
    id: 'ledger-pdf',
    hero: "Vision's PDF Analyzer",
    label: "Vision's PDF Analyzer",
    icon: ScanEye,
    title: "Vision's PDF Analyzer",
    description: 'Turn nominal-ledger PDFs from Taxcalc, Iris, Capium and Digita into transaction-ready Excel workbooks with AP/BK mapping.',
    accept: '.pdf',
    maxSizeLabel: '10MB per file • PDF',
    color: '#0e7490',
  },
  {
    id: 'ma-report',
    hero: "Pepper Potts' Report Studio",
    label: "Pepper Potts' Report Studio",
    icon: FileBarChart,
    title: "Pepper Potts' Report Studio",
    description: 'Build the management report PDF and PowerPoint from P&L, Balance Sheet and Chart of Accounts exports.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file',
    color: '#be185d',
  },
  {
    id: 'ma-report-v2',
    hero: "Morgan's Quarterly MA Studio",
    label: "Morgan's Quarterly MA Studio",
    icon: FileBarChart,
    title: "Morgan's Quarterly MA Studio",
    description: 'Build the quarterly management report PDF and PowerPoint from P&L, Balance Sheet and Chart of Accounts exports, with an Accountants Reports page and tax and dividends shown below EBT.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file',
    color: '#be185d',
  },
  {
    id: 'ma-report-monthly',
    hero: "Wasp’s MA Report & Excel",
    label: "Wasp’s MA Report & Excel",
    icon: TrendingDown,
    title: "Wasp’s MA Report & Excel",
    description: 'Monthly variance management report PDF from a P&L and Chart of Accounts export.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file',
    color: '#9333ea',
  },
  {
    id: 'as-workflow',
    hero: 'A&S Workflow Report',
    label: 'A&S Workflow Report',
    icon: Workflow,
    title: 'A&S Workflow Report',
    description: 'Live client / sub-client / phase status summary from the workflow system.',
    accept: '',
    maxSizeLabel: 'Live data: no upload needed',
    color: '#0369a1',
  },
  {
    id: 'reconciliation-tools',
    hero: 'Reconciliation Tools',
    label: 'Reconciliation Tools',
    icon: Scale,
    title: 'Reconciliation Tools',
    description: 'Pick a reconciliation tool: match settlement and payout reports against your own records.',
    accept: '',
    maxSizeLabel: 'Choose a tool to see its upload requirements',
    color: '#4f46e5',
  },
  {
    id: 'amazon-bendos-recon',
    hero: 'Amazon – Bendos Reconciliation',
    label: 'Amazon – Bendos Reconciliation',
    icon: ShoppingCart,
    title: 'Amazon – Bendos Reconciliation',
    description:
      'Reconcile Amazon settlement reports against Bendos order data, spot order-level mismatches and export an Excel report.',
    accept: '.csv,.xlsx,.xls',
    maxSizeLabel: '10MB per file • CSV, XLSX, XLS',
    color: '#2563eb',
  },
  {
    id: 'swiggy-zomato-recon',
    hero: 'Swiggy / Zomato Reconciliation',
    label: 'Swiggy / Zomato Reconciliation',
    icon: Utensils,
    title: 'Swiggy / Zomato Reconciliation',
    description:
      'Upload ERP data alongside Swiggy or Zomato provider reports to compute predicted vs. actual payouts, surface variances and download a detailed order-level report.',
    accept: '.xlsx',
    maxSizeLabel: '10MB per file • XLSX',
    color: '#f97316',
  },
  {
    id: 'property-recon',
    hero: 'Property / OTA Reconciliation',
    label: 'Property / OTA Reconciliation',
    icon: Building2,
    title: 'Property / OTA Reconciliation',
    description:
      "Upload StayLedger's booking register alongside Airbnb, Booking.com, MakeMyTrip or Agoda exports to match bookings by ID, flag missing or mismatched entries and export a multi-tab Excel report.",
    accept: '.csv,.xlsx,.xls',
    maxSizeLabel: '10MB per file • CSV, XLSX, XLS',
    color: '#7c3aed',
  },
  {
    id: 'quickcomm-recon',
    hero: 'Quick Commerce Reconciliation',
    label: 'Quick Commerce Reconciliation',
    icon: Zap,
    title: 'Quick Commerce Reconciliation',
    description:
      'Upload ERP sales/stock alongside Blinkit or Zepto settlement/stock reports to reconcile payouts and inventory, catch cancellations, exchanges and aging stock, and export a full Excel report.',
    accept: '.csv,.xlsx,.xls',
    maxSizeLabel: '10MB per file • CSV, XLSX, XLS',
    color: '#059669',
  },
  {
    id: 'ecommerce-recon',
    hero: 'E-commerce Reconciliation',
    label: 'E-commerce Reconciliation',
    icon: Store,
    title: 'E-commerce Reconciliation',
    description:
      'Upload SAP sales alongside Amazon, Flipkart, Meesho or Myntra settlement reports to match orders by ID, catch payout mismatches and RTO/return exposure, net exchange pairs and export a full Excel report.',
    accept: '.csv,.xlsx,.xls',
    maxSizeLabel: '10MB per file • CSV, XLSX, XLS',
    color: '#db2777',
  },
  {
    id: 'paygw-recon',
    hero: 'Payment Gateways Reconciliation',
    label: 'Payment Gateways Reconciliation',
    icon: CreditCard,
    title: 'Payment Gateways Reconciliation',
    description:
      'Upload your ERP sales report alongside settlement reports from Gokwik, PayU, Razorpay or PayPal (and a bank statement) to match orders and settlements, verify fees and confirm payouts landed in the bank.',
    accept: '.csv,.xlsx,.xls',
    maxSizeLabel: '10MB per file • CSV, XLSX, XLS',
    color: '#0f766e',
  },
];

export default missions;
